import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { STAFF_NAV } from "@/lib/staff-nav";
import { OperationsBanner } from "@/components/staff/OperationsBanner";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Doc { id: string; name: string; created_at: string; size: number | null }

export const Route = createFileRoute("/staff/documents")({
  head: () => ({
    meta: [
      { title: "Document Upload — Operations" },
      { name: "description", content: "Upload onboarding paperwork for household files." },
      { property: "og:title", content: "Document Upload — Operations" },
      { property: "og:description", content: "Upload onboarding paperwork for household files." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StaffDocuments,
});

function StaffDocuments() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Doc[]>([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    const { data } = await supabase
      .from("documents")
      .select("id, name, created_at, size")
      .order("created_at", { ascending: false })
      .limit(50);
    setRows((data as Doc[]) ?? []);
  };
  useEffect(() => { void load(); }, []);

  const upload = async (file: File) => {
    if (!user) return;
    setBusy(true);
    const path = `${user.id}/${Date.now()}-${file.name}`;
    const { error: upErr } = await supabase.storage.from("client-uploads").upload(path, file);
    if (upErr) { toast.error(upErr.message); setBusy(false); return; }
    const { error } = await supabase.from("documents").insert({
      owner_id: user.id,
      bucket: "client-uploads",
      path,
      name: file.name,
      mime_type: file.type || null,
      size: file.size,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Paperwork uploaded");
    void load();
  };

  return (
    <ProtectedRoute requiredRole="staff">
      <PortalShell title="Document Upload" nav={STAFF_NAV}>
        <OperationsBanner />
        <Eyebrow>Paperwork</Eyebrow>
        <h1 className="mt-3 font-serif text-3xl font-semibold text-navy-900">Document upload</h1>
        <p className="mt-2 font-sans text-sm text-charcoal-500">
          Upload onboarding paperwork. Private household vault files are not downloadable from operations.
        </p>

        <div className="mt-6 rounded-sm border border-dashed border-bronze-600/50 bg-white p-6 text-center">
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="min-h-11 rounded-sm border border-bronze-600 bg-bronze-600 px-6 font-sans text-[12px] font-semibold uppercase tracking-[0.08em] text-navy-900 disabled:opacity-60"
          >
            {busy ? "Uploading…" : "Choose a file"}
          </button>
        </div>

        <ul className="mt-6 divide-y divide-bronze-600/15 rounded-sm border border-bronze-600/30 bg-white">
          {rows.length === 0 && <li className="px-4 py-6 font-sans text-sm text-charcoal-500">No documents visible.</li>}
          {rows.map((d) => (
            <li key={d.id} className="px-4 py-3">
              <p className="font-sans text-sm text-charcoal-900">{d.name}</p>
              <p className="font-sans text-[11px] uppercase tracking-[0.14em] text-charcoal-500">
                {new Date(d.created_at).toLocaleDateString("en-US", { dateStyle: "medium" } as Intl.DateTimeFormatOptions)}
              </p>
            </li>
          ))}
        </ul>
      </PortalShell>
    </ProtectedRoute>
  );
}
